import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  TableCell,
  TableRow,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  InputLabel,
  Chip,
  Alert,
} from "@mui/material";
import { FC, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useStyles from "../../../clientAdmin/components/lead/quote/leadEstimateStyle";
import RenderEstimateTable from "../../../clientAdmin/components/lead/estimate/RenderEstimateTable";
import { getTotal } from "../../../clientAdmin/components/lead/quote/leadEstimateUtil";

import FormField from "../../../components/common/FormField";
import { FORM_FIELD_STATE } from "./confirmationUtils";
import { updateQuoteConfirmAction } from "../../../store/reducers/customerSlice";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  customerMaterialsFields,
  customerMaterialsHeading,
  customerQuoteChargesFields,
  customerQuoteChargesHeading,
} from "./fomFieldData";
import {
  clientInsuranceFields,
  clientInsuranceGeneralFields,
} from "../confirmation/confirmationUtils";
import AlertPopup from "../../../components/common/alertPopup/AlertPopup";
import ConfirmationDetails from "../confirmation/ConfirmationDetails";

interface CustomerQuoteViewProps {
  leadDetails?: any;
}

const CustomerQuoteView: FC<CustomerQuoteViewProps> = ({ leadDetails }) => {
  const classes = useStyles({});
  const [activeQuoteTab, setActiveQuoteTab] = useState(0);
  const [quoteTabs, setQuoteTabs] = useState<any[]>([]);
  const [currentQuote, setCurrentQuote] = useState<any>(null);
  const [lastActiveTab, setLastActiveTab] = useState<number | null>(null);
  const [openNewQuoteConfirm, setOpenNewQuoteConfirm] = useState(false);
  const [contentValueError, setContentValueError] = useState(false);
  const dispatch = useDispatch();

  // Helper function to get property value (handles both camelCase and lowercase)
  const getProperty = (obj: any, camelCase: string, lowercase: string) => {
    return obj?.[camelCase] ?? obj?.[lowercase];
  };

  const getQuoteFieldValue = (quote: any, fieldId: string) => {
    const value = quote?.[fieldId];
    if (typeof value === "boolean") {
      return value;
    }
    return value ?? "";
  };

  const getTotalWithVat = (quote: any, vatIncluded: boolean) => {
    const rawTotal = getProperty(quote, "quoteTotal", "quotetotal");
    const normalizedTotal =
      typeof rawTotal === "string"
        ? Number(rawTotal.replace(/,/g, ""))
        : Number(rawTotal);

    if (!Number.isFinite(normalizedTotal)) return "";

    if (vatIncluded) {
      return normalizedTotal.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      return (normalizedTotal * 1.2).toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  };

  const { customerLeadDetails, customerConfirmQuoteDetails } = useSelector(
    (state: any) => state.customer,
  );

  // Handle quote tabs and data
  useEffect(() => {
    if (
      customerConfirmQuoteDetails &&
      customerConfirmQuoteDetails.quotes &&
      customerConfirmQuoteDetails.quotes.length > 0
    ) {
      const quotesArray = customerConfirmQuoteDetails.quotes;

      const newTabs = quotesArray.map((quote: any, index: number) => ({
        id: index,
        label: `${index + 1}-${
          quote.created_at
            ? new Date(quote.created_at).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "2-digit",
              })
            : "Quote"
        }`,
        data: quote,
      }));

      setQuoteTabs(newTabs);

      const restoreTab =
        lastActiveTab !== null && lastActiveTab < quotesArray.length
          ? lastActiveTab
          : 0;

      const selectedQuote = quotesArray[restoreTab];

      setCurrentQuote(selectedQuote);
      setActiveQuoteTab(restoreTab);

      // IMPORTANT
      setQuoteFormData(selectedQuote);

      setLastActiveTab(null);
    } else {
      setQuoteTabs([]);
      setCurrentQuote(null);
    }
  }, [customerConfirmQuoteDetails]);

  const handleQuoteTabChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setActiveQuoteTab(newValue);
    setLastActiveTab(newValue);

    const newQuote = quoteTabs[newValue]?.data || null;

    setCurrentQuote(newQuote);

    if (newQuote) {
      setQuoteFormData(newQuote);
    }
  };

  const hasQuotes = quoteTabs && quoteTabs.length > 0 && currentQuote;
  const [formData, setFormData] = useState<any>({
    leadId: "",
    quoteId: "",
    contentValue: null,
    comments: "",
    isAccepted: false,
    isTermAccepted: false,
    isInsuranceAccepted: false,
  });

  const isNontDisabled = ["QUOTE", "CONFIRMED"].includes(
    String(leadDetails?.status || "").toUpperCase(),
  );

  const setQuoteFormData = (quote: any) => {
    setContentValueError(false);
    setFormData({
      leadId: customerLeadDetails?.lead?.generated_id || "",
      quoteId: quote?.quoteid || quote?.id || "",
      contentValue: quote?.contentValue || null,
      comments: quote?.comments || "",
      isAccepted: quote?.acceptedStatus === "Accepted",
      isTermAccepted: !!quote?.isTermAccepted,
      isInsuranceAccepted:
        typeof quote?.isInsuranceAccepted === "boolean"
          ? quote?.isInsuranceAccepted
          : String(quote?.isInsuranceAccepted) === "true",
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prevData: any) => ({
      ...prevData,
      [field]: value,
    }));

    if (field === "contentValue" && String(value ?? "").trim()) {
      setContentValueError(false);
    }
  };

  const onQuoteFieldChange = (
    event: any,
    section: string,
    index: number,
    valueFormat?: string,
  ) => {
    const { id, value, checked } = event.target;

    setCurrentQuote((prevQuote: any) => {
      if (!prevQuote || !section) return prevQuote;

      const sectionRows = Array.isArray(prevQuote?.[section])
        ? [...prevQuote[section]]
        : [];

      const rowData = { ...(sectionRows[index] || {}) };

      const formattedValue =
        id === "isChargeable"
          ? checked
          : id === "status"
            ? String(value || "").toLowerCase()
            : valueFormat === "number" && value !== ""
              ? Number(value)
              : value;

      rowData[id] = formattedValue;

      if (section === "services" && id === "status") {
        const normalizedStatus = String(formattedValue || "")
          .trim()
          .toLowerCase();

        if (["accept", "accepted"].includes(normalizedStatus)) {
          rowData.jobStatus = "awaiting";
        }

        if (["decline", "declined"].includes(normalizedStatus)) {
          rowData.jobStatus = null;
        }
      }

      sectionRows[index] = rowData;

      const updatedQuote = {
        ...prevQuote,
        [section]: sectionRows,
      };

      setQuoteTabs((prevTabs: any[]) =>
        prevTabs.map((tab: any, tabIndex: number) =>
          tabIndex === activeQuoteTab ? { ...tab, data: updatedQuote } : tab,
        ),
      );

      return updatedQuote;
    });
  };

  const submitQuote = (isGenerateNewQuote: boolean) => {
    setLastActiveTab(activeQuoteTab); // Track last active tab before save
    const submissionData = {
      ...formData,
      isTermAccepted: formData.isTermAccepted,
      isInsuranceAccepted: formData.isInsuranceAccepted,
      isGenerateNewQuote,
      contentValue: formData.contentValue || null,
      quoteId: currentQuote?.quoteId || currentQuote?.id || formData.quoteId,
      leadId: customerLeadDetails?.lead?.generated_id || formData.leadId,
      services: currentQuote?.services || [],
    };

    console.log("Form submitted with data:", submissionData);
    dispatch(updateQuoteConfirmAction(submissionData));
  };

  const handleNewQuoteConfirm = (isGenerateNewQuote: boolean) => {
    setOpenNewQuoteConfirm(false);
    submitQuote(isGenerateNewQuote);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    if (!formData.isTermAccepted) {
      console.error("Please accept the quote and terms");
      return;
    }

    if (
      formData.isInsuranceAccepted &&
      !String(formData.contentValue ?? "").trim()
    ) {
      setContentValueError(true);
      console.error("Content value is required when insurance is accepted");
      return;
    }

    setContentValueError(false);

    const hasDeclinedService = (currentQuote?.services || []).some(
      (service: any) => {
        const status = String(service?.status || "")
          .trim()
          .toLowerCase();
        return status === "decline" || status === "declined";
      },
    );

    const insuranceAmount = Number(currentQuote?.insuranceAmount || 0);

    const insurancePercentage = Number(currentQuote?.insurancePercentage || 0);

    // If any service is declined and insurance is not accepted, show modal
    if (
      (insuranceAmount > 0 &&
        insurancePercentage > 0 &&
        !formData?.isInsuranceAccepted) ||
      hasDeclinedService
    ) {
      console.log("OPEN MODAL");
      setOpenNewQuoteConfirm(true);
      return;
    }

    submitQuote(false);
  };

  const toNumber = (value: any) => {
    if (value === null || value === undefined) return 0;
    const numeric = Number(String(value).replace(/,/g, "").trim());
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const getCancellationCharge = (quoteTotal: any, percent: any) => {
    return ((toNumber(quoteTotal) * toNumber(percent)) / 100).toFixed(2);
  };

  const cancellationFees = currentQuote?.financialInfo?.cancellation_fees || [];
  const feeOne = cancellationFees[0];
  const feeTwo = cancellationFees[1];
  const totalQuoteValue = currentQuote?.quoteTotal ?? currentQuote?.quotetotal;

  const isQuoteExpired =
    currentQuote?.quoteExpiresOn &&
    dayjs(currentQuote.quoteExpiresOn).isBefore(dayjs(), "day");


  return (
    <Box sx={{ width: "100%" }}>
      {!hasQuotes ? (
        <Box
          sx={{
            textAlign: "center",
            padding: "40px 20px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: "16px",
              color: "#637381",
              marginBottom: "16px",
              fontWeight: 500,
            }}
          >
            No quotes/confirmation available yet
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              color: "#919EAB",
              lineHeight: 1.6,
            }}
          >
            Once your survey is completed, we will provide you with a detailed
            quote/confirmation for your removal.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Box
            sx={{
              position: "relative",
              // marginTop: "8px",
              padding: "16px 32px",
              border: "2px solid #f4f4f4",
              borderRadius: "10px",
              background: "#FFF",
            }}
          >
            <ConfirmationDetails formData={currentQuote} />

            {currentQuote?.financialInfo &&
              currentQuote?.financialInfo?.is_cancellation_fees_allowed && (
                <Alert sx={{ mt: 2 }} severity="info">
                  <b>Warning:</b> You should be aware that setting the date as
                  Confirmed and then moving it up to {feeOne?.days || 0} before
                  the service date will incur a cost of £
                  {getCancellationCharge(totalQuoteValue, feeOne?.percent)}
                  {feeTwo
                    ? ` and moving the date up to ${feeTwo?.days || 0} days before the service date will incur a charge of £${getCancellationCharge(totalQuoteValue, feeTwo?.percent)}.`
                    : "."}
                </Alert>
              )}
          </Box>
          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              // opacity: load ? 1 : 0,
              paddingLeft: "0px !important",
              paddingTop: "0px !important",
              backgroundColor: "#E5F4F1",
              borderRadius: "5px",
            }}
          >
            {/* Tabs Section - Above Services */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                backgroundColor: "#fff",
                padding: "10px 16px",
                gap: 1,
                marginBottom: "10px",
                borderRadius: "5px 5px 0 0",
              }}
            >
              <Tabs
                value={activeQuoteTab}
                onChange={handleQuoteTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    padding: "8px 16px",
                  },
                }}
              >
                {quoteTabs.map((tab, index) => {
                  const tabStatus =
                    index === activeQuoteTab
                      ? currentQuote?.acceptedStatus?.toLowerCase()
                      : (tab?.data as any)?.acceptedStatus?.toLowerCase();

                  const isQuoted = tabStatus === "quoted";
                  const isAccepted = tabStatus === "accepted";

                  const bgColor = isAccepted
                    ? "#E3F2FD"
                    : isQuoted
                      ? "#E8F5E9"
                      : "#F5F5F5";
                  const txtColor = isAccepted
                    ? "#1565C0"
                    : isQuoted
                      ? "#2E7D32"
                      : "#757575";

                  return (
                    <Tab
                      key={tab.id}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <span>
                            {tab.label}
                            <br />
                            <Chip
                              label={tabStatus || "Initiated"} // ✅ each tab's own status
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontSize: "10px",
                                height: "16px",
                                backgroundColor: "#E0E0E0",
                                color: txtColor,
                              }}
                            />
                          </span>
                        </Box>
                      }
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    />
                  );
                })}
              </Tabs>
            </Box>

            {/* Quote Header - Quote Ref and Expiry Date */}
            <Box
              sx={{
                padding: "10px 16px",
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                pointerEvents: isNontDisabled ? "auto" : "none",
                opacity: isNontDisabled ? 1 : 0.7,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  color: "#000",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  display: "inline-block",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                This quote expires on{" "}
                {getProperty(currentQuote, "quoteExpiresOn", "quoteexpireson")
                  ? new Date(
                      getProperty(
                        currentQuote,
                        "quoteExpiresOn",
                        "quoteexpireson",
                      ),
                    ).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : "N/A"}
              </Box>
              <Typography
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  color: "#000",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  display: "inline-block",
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                {currentQuote?.vatIncluded
                  ? "Prices include VAT"
                  : "Prices exclude VAT"}
              </Typography>
            </Box>

            <div className={classes.container} style={{ paddingBottom: 10 }}>
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  sx={{ paddingLeft: "0px !important" }}
                >
                  <div
                    className={classes.container}
                    style={{
                      // height: "calc(100% - 45px)",
                      paddingBottom: 0,
                      border: "1px solid #DDD",
                      pointerEvents:
                        isQuoteExpired ||
                        currentQuote?.acceptedStatus?.toLowerCase() ===
                          "declined" ||
                        !["accepted", "quoted"].includes(
                          currentQuote?.acceptedStatus?.toLowerCase(),
                        )
                          ? "none"
                          : "auto",
                      opacity: isQuoteExpired ? 0.7 : 1,
                    }}
                  >
                    {/* <Typography variant="subtitle1">Services</Typography> */}
                    <RenderEstimateTable
                      {...{
                        estimateForm: currentQuote,
                        tableHeadding: customerQuoteChargesHeading,
                        tableContent: currentQuote?.services,
                        staticLabelRows: 1,
                        fields: customerQuoteChargesFields.map((field) => ({
                          ...field,
                          disabled: ({ row, idx, estimateForm }: any) => {
                            if (field?.isDisabled) return true;

                            const fieldAcceptedStatus = String(
                              currentQuote?.acceptedStatus || "",
                            )
                              .trim()
                              .toLowerCase();

                            // Common quote-level disable condition
                            const shouldDisableQuoteFields =
                              fieldAcceptedStatus === "declined" ||
                              (fieldAcceptedStatus === "accepted" &&
                                ((estimateForm?.isTermAccepted === true &&
                                  estimateForm?.isReacceptRequired === false) ||
                                  (estimateForm?.isTermAccepted === false &&
                                    estimateForm?.isReacceptRequired ===
                                      true)));

                            // Status field
                            if (field.id === "status") {
                              return shouldDisableQuoteFields;
                            }

                            // Date and Job Status
                            if (["date", "jobStatus"].includes(field?.id)) {
                              const currentStatus = String(
                                estimateForm?.services?.[idx]?.status ??
                                  row?.status ??
                                  "",
                              )
                                .trim()
                                .toLowerCase();

                              // If service is declined, disable date/jobStatus
                              if (
                                currentStatus === "decline" ||
                                currentStatus === "declined"
                              ) {
                                return true;
                              }

                              // If quote-level condition is true, disable date/jobStatus
                              return shouldDisableQuoteFields;
                            }

                            return false;
                          },
                        })),
                        additionRow: (
                          <>
                            {currentQuote?.services?.length >= 2 &&
                              currentQuote?.services?.filter(
                                (service: any) =>
                                  !service.isAccepted &&
                                  ![
                                    "Clearance",
                                    "Into Store",
                                    "Out Of Store",
                                    "Removal",
                                    "Full Pack",
                                    "Unpack",
                                    "Dismantle",
                                    "Fragile Pack",
                                    "Vehicle Hire",
                                    "Export Pack",
                                    "Storage",
                                  ].includes(service.typeName),
                              ).length < 3 && (
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      paddingTop: "10px!important",
                                    }}
                                  ></TableCell>
                                  <TableCell colSpan={9}></TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      paddingTop: "10px!important",
                                    }}
                                  ></TableCell>
                                </TableRow>
                              )}

                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  paddingTop: "20px!important",
                                }}
                              >
                                Total
                              </TableCell>
                              {/* <TableCell></TableCell> */}
                              <TableCell></TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  paddingTop: "20px!important",
                                  paddingLeft: "10px !important",
                                  textAlign: "left",
                                }}
                              >
                                £
                                {getTotal(
                                  "serivesCost",
                                  currentQuote,
                                ).toLocaleString("en-GB")}
                              </TableCell>
                            </TableRow>
                          </>
                        ),
                        section: "services",
                        onChangeAction: onQuoteFieldChange,
                        hideExtraServiceDelete: false,
                        // disabled: false,
                      }}
                    />
                  </div>
                </Grid>
              </Grid>
            </div>
          </Grid>
          {/* Material */}
          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              // opacity: load ? 1 : 0,
              paddingLeft: "0px !important",
              paddingTop: "10px !important",
            }}
          >
            <div
              className={classes.container}
              style={{
                backgroundColor: currentQuote?.requirements?.some(
                  (req: any) =>
                    req.service_name === "material" && req.is_accepted,
                )
                  ? "#E3FCEF"
                  : "none",
                background: "#E5F4F1",
              }}
            >
              {" "}
              <RenderEstimateTable
                {...{
                  estimateForm: currentQuote,
                  tableHeadding: customerMaterialsHeading,
                  tableContent: currentQuote?.materials,
                  fields: customerMaterialsFields,
                  additionRow: (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell colSpan={2}>
                        <FormControlLabel
                          label="Material Price Chargeable"
                          labelPlacement="start"
                          control={
                            <Checkbox
                              sx={{
                                marginTop: "16px",
                                marginLeft: "6px !important",
                                width: "auto !important",
                              }}
                              checked={
                                getProperty(
                                  currentQuote,
                                  "materialPriceChargeable",
                                  "materialpricechargeable",
                                ) || false
                              }
                              disabled={true}
                              // onChange={(e: any) => onChangeAction(e)}
                              name="materialPriceChargeable"
                              id="materialPriceChargeable"
                              color="primary"
                            />
                          }
                          sx={{
                            marginLeft: "0px !important",
                            "& .MuiFormControlLabel-label": {
                              fontSize: "13px", // Set your desired font size here
                              display: "flex",
                            },
                          }}
                        />
                      </TableCell>
                      {/* <TableCell></TableCell> */}
                      <TableCell sx={{ fontWeight: 600 }}>
                        £
                        {getTotal("total", currentQuote).toLocaleString(
                          "en-GB",
                        )}
                      </TableCell>

                      {/* <TableCell sx={{ fontWeight: 600 }}>
                        {getTotal("volume", currentQuote)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        £
                        {getTotal("cost", currentQuote).toLocaleString("en-IN")}
                      </TableCell> */}
                    </TableRow>
                  ),
                  section: "materials",
                  disabled: true,
                }}
              />
            </div>
          </Grid>
          {/* notes and comments side by side */}
          <Grid container spacing={2} sx={{ marginTop: "10px" }}>
            {/* Customer Notes (6 columns) */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                padding: "6px 10px 10px 10px !important",
                background: "#E5F4F1",
                borderRadius: "5px",
                border: "1px solid #ddd",
              }}
            >
              <Grid container spacing={1}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  style={{
                    padding: "10px 16px 10px 0px",
                    display: "flex-inline",
                    alignItems: "center",
                    paddingRight: "16px",
                  }}
                >
                  <InputLabel
                    sx={{
                      color: "#637381",
                      fontWeight: 600,
                      fontSize: "16px",
                      textAlign: "left !important",
                    }}
                    shrink
                  >
                    Quote Note (for customers)
                  </InputLabel>
                  <TextField
                    fullWidth
                    name="quoteNote"
                    label=""
                    value={currentQuote ? currentQuote.quoteNote || "" : ""}
                    variant="outlined"
                    sx={{ marginTop: "0px !important" }}
                    multiline
                    rows={3}
                    InputProps={{
                      sx: {
                        padding: "8px",
                        background: "#fff",
                      },
                    }}
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
            {/* Comments (6 columns) */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                border: "1px solid #CFCFCF",
                background: "#E5F4F1",
                borderRadius: "5px",
                paddingLeft: "0px",
                padding: "16px 0px",
              }}
            >
              <Grid container spacing={2} rowGap={0} rowSpacing={0}>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  style={{
                    padding: "10px 16px 10px 0px",
                    display: "flex-inline",
                    alignItems: "center",
                    paddingRight: "16px",
                  }}
                >
                  <InputLabel
                    sx={{
                      color: "#637381",
                      fontWeight: 600,
                      fontSize: "16px",
                      textAlign: "left !important",
                    }}
                    shrink
                  >
                    Comments
                  </InputLabel>
                  <TextField
                    fullWidth
                    name="comments"
                    label=""
                    value={formData.comments || ""}
                    variant="outlined"
                    sx={{ marginTop: "0px !important" }}
                    disabled={
                      currentQuote?.acceptedStatus?.toLowerCase() ===
                        "accepted" ||
                      currentQuote?.acceptedStatus?.toLowerCase() === "declined"
                    }
                    multiline
                    rows={3}
                    InputProps={{
                      sx: {
                        padding: "8px",
                        background: "#fff",
                      },
                    }}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        comments: event.target.value,
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* comment and insurance */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{
              border: "1px solid #CFCFCF",
              background: "#E5F4F1",
              borderRadius: "5px",
              paddingLeft: "0px",
              padding: "16px 0px",
            }}
          >
            <Grid
              container
              spacing={2}
              rowGap={0}
              rowSpacing={0}
              sx={{ paddingRight: "16px" }}
            >
              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: "flex", paddingLeft: "0px !important" }}
              >
                <Grid
                  container
                  spacing={0}
                  sx={{
                    display: "flex",
                  }}
                >
                  <>
                    {clientInsuranceGeneralFields.map(
                      (field: any, idx: number) => {
                        return (
                          <FormField
                            key={idx}
                            onPaste={(
                              e: React.ClipboardEvent<HTMLInputElement>,
                            ) => e.preventDefault()}
                            {...{
                              ...field,
                              variant: "outlined", // Adding this variant prop
                              isNonMarginTop: true,
                              inSingleGridForm: true,
                              value: getQuoteFieldValue(
                                currentQuote,
                                field?.id,
                              ),
                              // value: selectedQuotation
                              //   ? selectedQuotation?.[field?.id]
                              //   : "",
                              disabled:
                                true ||
                                isQuoteExpired ||
                                currentQuote?.acceptedStatus?.toLowerCase() ===
                                  "declined",
                              InputProps: {
                                ...(field?.inputProps && field?.inputProps),
                                autocomplete: "off",

                                sx: {
                                  padding: "2px 4px",
                                  height: "36px",
                                  // width:'120px'
                                },
                              },
                            }}
                          />
                        );
                      },
                    )}
                  </>
                </Grid>
                {/* <Typography
                            style={{
                              marginLeft: "2px",
                              display: "flex",
                              alignItems: "center",
                              fontWeight: 600,
                            }}
                          >
                            %
                          </Typography> */}
              </Grid>
              <Grid
                item
                xs={12}
                md={8}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingLeft: "5px !important",
                }}
              >
                {clientInsuranceFields.map((field: any, idx: number) => {
                  if (field.id === "isInsuranceAccepted") {
                    return (
                      <FormField
                        key={idx}
                        {...field}
                        type={field?.type}
                        value={String(formData.isInsuranceAccepted)}
                        onChange={(e: any) =>
                          handleInputChange(
                            "isInsuranceAccepted",
                            String(e?.target?.value) === "true",
                          )
                        }
                        disabled={
                          field.disabled ||
                          isQuoteExpired ||
                          currentQuote?.acceptedStatus?.toLowerCase() ===
                            "declined" ||
                          currentQuote?.acceptedStatus?.toLowerCase() ===
                            "accepted"
                        }
                        inSingleGridForm={true}
                        GridFieldStyle={{
                          paddingRight: idx === 0 ? "4px" : "0px",
                        }}
                      />
                    );
                  }
                  return (
                    <FormField
                      key={idx}
                      onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                        e.preventDefault()
                      }
                      {...field}
                      type={field?.type}
                      variant="outlined"
                      isNonMarginTop={true}
                      value={getQuoteFieldValue(currentQuote, field?.id)}
                      disabled={
                        field.disabled ||
                        isQuoteExpired ||
                        currentQuote?.acceptedStatus?.toLowerCase() ===
                          "declined" ||
                        currentQuote?.acceptedStatus?.toLowerCase() ===
                          "accepted"
                      }
                      inSingleGridForm={true}
                      GridFieldStyle={{
                        paddingRight: idx === 0 ? "4px" : "0px",
                      }}
                      InputProps={{
                        ...field?.inputProps,
                        autocomplete: "off",
                        startAdornment: (
                          <span
                            style={{
                              marginRight: "0px",
                              paddingTop: "1px",
                              fontWeight: 600,
                              fontSize: "18px",
                            }}
                          >
                            £
                          </span>
                        ),
                        sx: {
                          padding: "2px 4px",
                          height: "36px",
                        },
                      }}
                    />
                  );
                })}
              </Grid>
            </Grid>
            {/* Content Value Total */}
            <Grid container spacing={0} rowGap={0} rowSpacing={0}>
              <Grid
                item
                xs={12}
                sm={4}
                style={{
                  paddingRight: "16px",
                  display: "flex-inline",
                  alignItems: "center",
                }}
              >
                <InputLabel
                  sx={{
                    color: "#637381",
                    fontWeight: 600,
                    fontSize: "16px",
                    textAlign: "left !important",
                  }}
                  shrink
                >
                  Quote Total
                </InputLabel>
                <TextField
                  fullWidth
                  type="text"
                  name="quoteTotal"
                  label=""
                  value={
                    currentQuote?.quoteTotal
                      ? Number(currentQuote.quoteTotal).toFixed(2)
                      : ""
                  }
                  // value={selectedQuotation?.quoteTotal}
                  variant="outlined"
                  sx={{ marginTop: "0px !important" }}
                  disabled={true}
                  InputProps={{
                    autoComplete: "off",
                    startAdornment: (
                      <span
                        style={{
                          marginRight: "6px",
                          paddingTop: "1px",
                          fontWeight: 500,
                          fontSize: "16px",
                        }}
                      >
                        £
                      </span>
                    ),
                    sx: {
                      padding: "2px 4px",
                      height: "36px",
                      // width: "100px",
                      background: "#fff",
                    },
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={4}
                style={{
                  paddingRight: "16px",
                  display: "flex-inline",
                  alignItems: "center",
                }}
              >
                <InputLabel
                  sx={{
                    color: "#637381",
                    fontWeight: 600,
                    fontSize: "16px",
                    textAlign: "left !important",
                  }}
                  shrink
                >
                  With VAT Total (20%)
                </InputLabel>
                <TextField
                  fullWidth
                  type="text"
                  name="quoteTotal"
                  label=""
                  value={getTotalWithVat(
                    currentQuote,
                    currentQuote?.vatIncluded,
                  )}
                  // value={selectedQuotation?.quoteTotal}
                  variant="outlined"
                  sx={{ marginTop: "0px !important" }}
                  disabled={true}
                  InputProps={{
                    autoComplete: "off",
                    startAdornment: (
                      <span
                        style={{
                          marginRight: "6px",
                          paddingTop: "1px",
                          fontWeight: 500,
                          fontSize: "16px",
                        }}
                      >
                        £
                      </span>
                    ),
                    sx: {
                      padding: "2px 4px",
                      height: "36px",
                      // width: "100px",
                      background: "#fff",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          {/* payment */}
          <Grid
            container
            spacing={1}
            sx={{
              marginLeft: "0px !important",
              width: "100% !important",
              paddingTop: "10px",
            }}
          >
            {FORM_FIELD_STATE.map((field) => (
              <Grid container item xs={12} spacing={1} key={field.id}>
                {/* Label */}
                <Grid
                  item
                  xs={12}
                  sm={2}
                  className={classes.labelBox}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "14px !important",
                  }}
                >
                  <InputLabel
                    shrink
                    htmlFor={field.id}
                    sx={{
                      color: "#333",
                      fontWeight: 600,
                      fontSize: "17px",
                      lineHeight: 1.1,
                    }}
                  >
                    {field.label}
                  </InputLabel>
                </Grid>

                {/* Field */}
                <Grid
                  item
                  xs={12}
                  sm={field.md === 6 ? 2 : field.md}
                  sx={{ paddingLeft: "16px !important" }}
                >
                  <FormField
                    id={field.id}
                    type={field.type}
                    isNonMarginTop
                    value={
                      field.id === "contentValue"
                        ? formData.contentValue
                        : (currentQuote?.[field.id] ?? "")
                    }
                    disabled={
                      field.disabled ||
                      currentQuote?.acceptedStatus?.toLowerCase() ===
                        "declined" ||
                      currentQuote?.acceptedStatus?.toLowerCase() === "accepted"
                    }
                    required={formData.isInsuranceAccepted}
                    error={field.id === "contentValue" && contentValueError}
                    // helperText={
                    //   field.id === "contentValue" && contentValueError
                    //     ? "Content value is required when insurance is accepted"
                    //     : ""
                    // }
                    inputProps={field.inputProps}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (field.id === "contentValue") {
                        const val = e.target.value;
                        if (val === "" || /^\d*\.?\d*$/.test(val)) {
                          handleInputChange("contentValue", val);
                        }
                      }
                    }}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                      e.preventDefault()
                    }
                    InputProps={{
                      sx: {
                        padding: "2px 4px",
                        height: "36px",
                        // backgroundColor: field.disabled ? "#f5f5f5" : "#fff",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
          {/* Terms and Conditions */}
          <Grid
            container
            sx={{
              borderTop: "2px solid #F0EFF0",
              paddingTop: 3,
              marginTop: 2,
              paddingBottom: 2,
            }}
          >
            {currentQuote?.acceptedStatus?.toLowerCase() !== "declined" && (
              <Grid item md={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={"start"}
                >
                  <Checkbox
                    sx={{
                      padding: "0px 9px",
                    }}
                    checked={formData.isTermAccepted}
                    onChange={handleCheckboxChange}
                    name="isTermAccepted"
                    disabled={
                      (currentQuote?.acceptedStatus?.toLowerCase() ===
                        "accepted" &&
                        currentQuote?.isTermAccepted &&
                        !currentQuote?.isReacceptRequired) ||
                      currentQuote?.acceptedStatus?.toLowerCase() ===
                        "declined" ||
                      (isQuoteExpired && !currentQuote?.isReacceptRequired)
                    }
                  />
                  <Typography sx={{ fontWeight: 600, fontSize: "16px" }}>
                    I have read the quotation, accept the above details, and
                    have read the Terms and Conditions
                    <span style={{ color: "red", fontWeight: 500 }}>*</span>
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid
              item
              md={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "end",
              }}
            >
              {/* Submit Button */}
              {/* {!isDisabled && ( */}
              {!(
                (currentQuote?.acceptedStatus?.toLowerCase() === "accepted" &&
                  currentQuote?.isTermAccepted &&
                  !currentQuote?.isReacceptRequired) ||
                currentQuote?.acceptedStatus?.toLowerCase() === "declined" ||
                (isQuoteExpired && !currentQuote?.isReacceptRequired)
              ) && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ marginTop: 4, width: "160px", height: "40px" }}
                  onClick={(e) => handleSubmit(e)}
                  disabled={
                    (currentQuote?.acceptedStatus?.toLowerCase() ===
                      "accepted" &&
                      currentQuote?.isTermAccepted &&
                      !currentQuote?.isReacceptRequired) ||
                    currentQuote?.acceptedStatus?.toLowerCase() ===
                      "declined" ||
                    !formData.isTermAccepted ||
                    // Disable if any 'Removal' service is not accepted
                    (Array.isArray(currentQuote?.services)
                      ? currentQuote.services.some(
                          (service: any) =>
                            (service?.type?.toLowerCase() === "removal" ||
                              service?.typeName?.toLowerCase() === "removal") &&
                            String(service?.status || "")
                              .trim()
                              .toLowerCase() !== "accept" &&
                            String(service?.status || "")
                              .trim()
                              .toLowerCase() !== "accepted",
                        )
                      : false)
                  }
                >
                  {currentQuote?.isReacceptRequired ? "Re-Confirm" : "Confirm"}
                </Button>
              )}
              {/* )} */}
            </Grid>
          </Grid>
          {currentQuote?.acceptedStatus &&
            currentQuote?.acceptedStatus?.toLowerCase() === "accepted" &&
            !currentQuote?.isReacceptRequired && (
              <Alert sx={{ mt: 2 }} severity="info">
                <b>Info:</b> Your acceptance of these quotes has been recorded
                and you will be contacted soon. Thank you
              </Alert>
            )}
          {currentQuote?.quoteExpiresOn &&
            dayjs(currentQuote?.quoteExpiresOn).isBefore(dayjs(), "day") && (
              <Alert sx={{ mt: 2 }} severity="warning">
                <b>Warning:</b> This quote has expired and might not be valid.
                The office will contact you should there be any changes.
              </Alert>
            )}

          <AlertPopup
            open={openNewQuoteConfirm}
            title="Confirmation"
            content="Do you want a new quote?"
            onHide={() => handleNewQuoteConfirm(false)}
            submitHandler={() => handleNewQuoteConfirm(true)}
            yesText="Yes"
            noText="No"
          />
        </Grid>
      )}
    </Box>
  );
};

export default CustomerQuoteView;
